import { GetStaticPaths } from "next";
import { serverSideTranslations } from "next-i18next/pages/serverSideTranslations";
import { useRouter } from "next/router";
import type { ReactElement } from "react";

import { T_KEY } from "@/src/constants/translationsKey";

import HeadMeta from "../../../src/components/common/HeadMeta/HeadMeta";
import CenteredLayout from "../../../src/components/common/Layout/CenteredLayout";
import GuideContentBody from "../../../src/components/customer-service/Guide/Contents/Body/GuideContentBody";
import GuideContentHeader from "../../../src/components/customer-service/Guide/Contents/Header/GuideContentHeader";
import { CS_OG_MESSAGES } from "../../../src/constants/constants";
import { GUIDE_CONTENTS } from "../../../src/constants/constants_guideContents";
// 중복 import 제거 - GUIDE_CONTENTS에서만 가져오도록 수정
import useAppStateStore from "../../../src/store/client/appState";
import {
  ContentStatusEnum,
  GuideContent,
  Locale,
  LOCALES_SHORT,
} from "../../../src/utils/types";

import styles from "./guideDetail.module.scss";

interface GuideDetailProps {
  locale: Locale;
  data: GuideContent;
  id: string;
}

export default function GuideDetail({
  data,
  locale,
  id,
}: GuideDetailProps): ReactElement | null {
  const ogMessage =
    (locale && CS_OG_MESSAGES[`${locale}`]) || CS_OG_MESSAGES[LOCALES_SHORT.EN];

  const isGolffixApp = useAppStateStore(state => state.isGolffixApp);
  // isGolffixApp은 클라이언트 UA 감지 값이라 SSR에서는 항상 null — null을 웹 기본값으로
  // 렌더해야 크롤러가 메타·본문이 포함된 HTML을 받는다 (SD-8408)
  // ?webview=1: gf-web 리포트 바텀시트(iframe)처럼 UA 로 앱을 감지할 수 없는 임베드용 (SD-8616)
  const { query } = useRouter();
  const isWebView = isGolffixApp === true || query.webview === "1";
  const urlLocale =
    !locale || String(locale) === "__default" ? LOCALES_SHORT.EN : locale;

  return (
    <CenteredLayout
      locale={locale}
      showLogo={!isWebView}
      showFooter={!isWebView}
      isWebView={isWebView}
      showUserMenuOrLoginButton
      centered
      // ! : 고객센터 페이지에서는 사용자 메뉴와 로그인 버튼을 보여준다.
    >
      <div className={styles.guideDetailContainer}>
        <HeadMeta
          {...ogMessage}
          title={data?.title || ogMessage.title}
          image="/images/customer-service/og.png"
          url={`https://web.golffix.io/${urlLocale}/cs/faq/${id}`}
          locale={locale}
          isWebView={isWebView}
        />
        <div className={styles.guideContentHeaderContainer}>
          {data?.title && <GuideContentHeader title={data.title} />}
        </div>
        {data?.fields && data.fields.length > 0 && (
          <GuideContentBody fields={data.fields} />
        )}
      </div>
    </CenteredLayout>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  // GUIDE_CONTENTS를 직접 사용하여 순환 참조 방지
  const paths = Object.entries(GUIDE_CONTENTS).flatMap(([locale, contents]) =>
    Object.entries(contents)
      .filter(([, content]) => content.status === "published")
      .map(([id]) => ({
        params: { id },
        locale,
      }))
  );

  return { paths, fallback: false };
};

export const getStaticProps = async ({
  params,
  locale,
  defaultLocale,
}: {
  params: { id: string };
  locale: Locale;
  defaultLocale: Locale;
}) => {
  let content: GuideContent = {
    category: ["", ""],
    title: "",
    fields: [],
    status: ContentStatusEnum.Published,
  };
  let notFound = false;

  try {
    // eslint-disable-next-line security/detect-object-injection -- locale은 Next i18n 설정에 정의된 Locale union 값(getStaticProps 인자)
    const guideContents = GUIDE_CONTENTS[locale];
    content = guideContents[`${params.id}`];
    notFound = !guideContents[`${params.id}`];
  } catch (err) {
    console.error("getStaticProps error", err);
  }

  return {
    props: {
      ...(await serverSideTranslations(locale ?? defaultLocale, [T_KEY])),
      locale: locale ?? null, // locale 값이 undefined일 경우 null로 반환
      data: content,
      id: params.id, // canonical URL 자기참조용 (SD-8408)
    },
    revalidate: 5,
    notFound,
  };
};
